import { useQuery } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'
import { Button } from '~/components/common/Button'
import { Typography } from '~/components/common/Typography'
import ManageAccountDialog from '~/components/plan/ManageAccountDialog'
import { BillingProvider, useBillingContext } from '~/contexts/BillingContext'
import { SuperAdminBillingAccountOverrideProvider } from '~/contexts/BillingAccountsContext'
import { WorkspaceContext } from '~/contexts/WorkspaceContext'
import {
  getBillingAccountById,
  getBillingWorkspacesDetails,
  type BillingWorkspaceDetails
} from '~/services/superAdminService'
import type { Workspace } from '~/utils/workspaceSelection'
import { LicensesModal } from './AccountManagementLicensesModal'

function toWorkspaceStub(d: BillingWorkspaceDetails): Workspace {
  return {
    id: d.org_id,
    uuid: d.uuid,
    name: d.org_name,
    is_default: d.is_default,
    org_metadata_sso: {} as Workspace['org_metadata_sso'],
    org_session_timeout: 0,
    org_runtime_setting: {
      settings_xml_raw_txt: '',
      settings_security_xml_raw_txt: ''
    },
    user_role: 'admin',
    is_admin: true,
    account_type: d.org_type || '',
    onboarded: true,
    onboard_started: true
  }
}

export interface SuperAdminManageAccountDialogProps {
  isOpen: boolean
  onClose: () => void
  billingAccountId: number
}

/**
 * Full Manage Account experience (all tabs) for a billing account, for platform super admins.
 * Bridges billing selection and workspace list without changing the signed-in user's context.
 */
export function SuperAdminManageAccountDialog({
  isOpen,
  onClose,
  billingAccountId
}: SuperAdminManageAccountDialogProps) {
  const { data: workspacesDetails } = useQuery({
    queryKey: ['superAdminBillingWorkspaces', billingAccountId],
    queryFn: () => getBillingWorkspacesDetails(billingAccountId),
    enabled: isOpen && billingAccountId > 0
  })
  const { data: billingAccount, refetch: refetchBillingAccount } = useQuery({
    queryKey: ['superAdminBillingAccount', billingAccountId],
    queryFn: () => getBillingAccountById(billingAccountId),
    enabled: isOpen && billingAccountId > 0
  })

  const workspaceList = useMemo(
    () => (workspacesDetails ?? []).map(toWorkspaceStub),
    [workspacesDetails]
  )

  const workspaceContextValue = useMemo(
    () => ({
      workspaces: workspaceList,
      currentWorkspace: null as Workspace | null,
      isLoading: false,
      error: null as string | null,
      refetch: async () => {},
      switchToWorkspace: async () => {},
      createNewWorkspace: async () => {},
      switching: false,
      creating: false,
      updateCurrentWorkspaceRuntimeSetting: () => {}
    }),
    [workspaceList]
  )

  if (!billingAccountId) return null

  return (
    <SuperAdminBillingAccountOverrideProvider billingAccountId={billingAccountId}>
      <BillingProvider>
        <WorkspaceContext.Provider value={workspaceContextValue}>
          <SuperAdminManageAccountDialogContent
            isOpen={isOpen}
            onClose={onClose}
            billingAccount={billingAccount ?? null}
            onRefreshBillingAccount={() => {
              void refetchBillingAccount()
            }}
          />
        </WorkspaceContext.Provider>
      </BillingProvider>
    </SuperAdminBillingAccountOverrideProvider>
  )
}

function SuperAdminManageAccountDialogContent({
  isOpen,
  onClose,
  billingAccount,
  onRefreshBillingAccount
}: {
  isOpen: boolean
  onClose: () => void
  billingAccount: Awaited<ReturnType<typeof getBillingAccountById>> | null
  onRefreshBillingAccount: () => void
}) {
  const [isLicenseControlsOpen, setIsLicenseControlsOpen] = useState(false)
  const { refetchAllLicenses, refetchLicenseTypes } = useBillingContext()

  const handleLicenseControlsUpdate = () => {
    onRefreshBillingAccount()
    void refetchAllLicenses()
    void refetchLicenseTypes()
  }

  return (
    <>
      <ManageAccountDialog
        isOpen={isOpen}
        onClose={onClose}
        readOnly
        isSuperAdminImpersonation
        licenseTabAction={
          <div className='flex flex-col gap-3 rounded-[16px] border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white p-4 shadow-segment'>
            <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
              <div className='flex flex-col gap-1'>
                <Typography
                  fontFamily='geist'
                  fontSize='bodyMdMedium'
                  fontWeight='semibold'
                  className='text-tokens-neutral-text-primary select-none'
                >
                  Super admin license controls
                </Typography>
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  className='text-tokens-neutral-text-secondary'
                >
                  Create payment links, choose monthly or 6-month billing packages, and manage
                  pending licenses.
                </Typography>
              </div>
              <Button
                variant='outlined'
                onClick={() => setIsLicenseControlsOpen(true)}
                isDisabled={!billingAccount}
                className='h-9 shrink-0 px-3'
              >
                <Typography fontFamily='geist' fontSize='bodySm' fontWeight='medium'>
                  Open Controls
                </Typography>
              </Button>
            </div>
          </div>
        }
      />
      <LicensesModal
        isOpen={isLicenseControlsOpen}
        onClose={() => setIsLicenseControlsOpen(false)}
        account={billingAccount}
        onUpdate={handleLicenseControlsUpdate}
      />
    </>
  )
}
